import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// 기존 이미지 에셋 (유지)
import image01_1 from "../../assets/images/landing/01-1.png";
import image01 from "../../assets/images/landing/01.png";
import image02 from "../../assets/images/landing/02.png";
import image03 from "../../assets/images/landing/03.png";
import image04 from "../../assets/images/landing/04.png";
import image05 from "../../assets/images/landing/05.png";
import image06 from "../../assets/images/landing/06.png";
import imageAvatar1 from "../../assets/images/landing/avatar1.png";
import imageAvatar2 from "../../assets/images/landing/avatar2.png";
import imageAvatar3 from "../../assets/images/landing/avatar3.png";
import imageCrew from "../../assets/images/landing/crew.png";
import imageHero1 from "../../assets/images/landing/hero1.png";
import imageHero2 from "../../assets/images/landing/hero2.png";
import svgLogo from "../../assets/images/landing/logo.svg";
import svgNaverblog from "../../assets/images/landing/naverblog.svg";

import "../../assets/css/app_landing_v2.scss";
import Header from "../../components/header";
import Footer from "../../components/footer";

// side-nav 섹션 정보
const SECTION_LABELS = [
  "앱 다운로드",
  "키즈다 소개",
  "뉴스",
  "키즈 루틴",
  "AI 음성 일기",
  "키즈 마켓",
  "에듀클립",
  "브레인랩",
  "키즈다 크루",
  "한눈에 보기",
  "회사 정보",
];

// 어두운 배경 섹션 인덱스
const DARK_SECTION_INDICES = [9];
const ANIMATION_DURATION = 900;
const TOTAL_SECTIONS = SECTION_LABELS.length;

// 카운트업 애니메이션
function animateCount(el, target) {
  const match = target.match(/^([\d,]+)/);
  if (!match) {
    el.textContent = target;
    return;
  }
  const numStr = match[1].replace(/,/g, "");
  const num = parseInt(numStr, 10);
  const suffix = target.replace(match[1], "");
  const hasComma = match[1].includes(",");
  const duration = 1200;
  const startTime = performance.now();

  function update(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = Math.round(num * eased);
    const formatted = hasComma ? current.toLocaleString() : current.toString();
    el.textContent = formatted + suffix;
    if (progress < 1) requestAnimationFrame(update);
  }
  requestAnimationFrame(update);
}

const MOBILE_BREAKPOINT = 1024;

function isMobile() {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function AppLanding() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const sectionRefs = useRef([]);
  const currentIndexRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const wheelAccRef = useRef(0);
  const wheelTimeoutRef = useRef(null);
  const touchStartYRef = useRef(0);
  const [isMobileView, setIsMobileView] = useState(isMobile());

  const [activeIndex, setActiveIndex] = useState(0);
  const [isOnDark, setIsOnDark] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [headerOnDark, setHeaderOnDark] = useState(false);

  // section ref 등록 헬퍼
  const setSectionRef = useCallback((el, idx) => {
    if (el) sectionRefs.current[idx] = el;
  }, []);

  // scroll-reveal 요소 활성화 (staggered)
  const activateReveals = useCallback((section) => {
    if (!section) return;
    const reveals = section.querySelectorAll(".scroll-reveal");
    reveals.forEach((el, i) => {
      setTimeout(() => {
        el.classList.add("visible");
      }, i * 150);
    });
    // 통계 섹션 카운트업
    if (section.classList.contains("section-summary")) {
      section.querySelectorAll(".stat-item strong").forEach((el) => {
        animateCount(el, el.textContent);
      });
    }
  }, []);

  // scroll-reveal 비활성화
  const deactivateReveals = useCallback((section) => {
    if (!section) return;
    section.querySelectorAll(".scroll-reveal").forEach((el) => {
      el.classList.remove("visible");
    });
  }, []);

  // 인디케이터 + 헤더 상태 업데이트
  const updateIndicator = useCallback((index) => {
    setActiveIndex(index);
    const isDark = DARK_SECTION_INDICES.includes(index);
    setIsOnDark(isDark);
    setHeaderScrolled(index !== 0);
    setHeaderOnDark(isDark);
  }, []);

  // 섹션 이동 (풀페이지 스크롤)
  const goTo = useCallback(
    (index) => {
      if (isAnimatingRef.current) return;
      if (index < 0 || index >= TOTAL_SECTIONS) return;
      if (index === currentIndexRef.current) return;

      isAnimatingRef.current = true;
      const prevIndex = currentIndexRef.current;
      currentIndexRef.current = index;

      if (containerRef.current) {
        containerRef.current.style.transform = `translateY(-${index * 100}vh)`;
      }

      updateIndicator(index);

      setTimeout(() => {
        activateReveals(sectionRefs.current[index]);
      }, 200);

      setTimeout(() => {
        deactivateReveals(sectionRefs.current[prevIndex]);
        isAnimatingRef.current = false;
      }, ANIMATION_DURATION);
    },
    [activateReveals, deactivateReveals, updateIndicator],
  );

  const goNext = useCallback(() => goTo(currentIndexRef.current + 1), [goTo]);
  const goPrev = useCallback(() => goTo(currentIndexRef.current - 1), [goTo]);

  // 사이드 네비에서 클릭으로 이동
  const scrollToSection = useCallback(
    (idx) => {
      goTo(idx);
    },
    [goTo],
  );

  // resize 감지
  useEffect(() => {
    const onResize = () => setIsMobileView(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 초기화: 첫 섹션 reveal 활성화
  useEffect(() => {
    activateReveals(sectionRefs.current[0]);
    updateIndicator(0);

    if (!isMobileView) {
      // 데스크톱: body 고정 (풀페이지 스크롤)
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.height = "100%";
    } else {
      // 모바일: 일반 스크롤 — body 고정 해제
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      if (containerRef.current) {
        containerRef.current.style.transform = "none";
        containerRef.current.style.transition = "none";
      }
      // 모바일에서 모든 섹션 reveal 활성화
      sectionRefs.current.forEach((section) => {
        if (section) activateReveals(section);
      });
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, [activateReveals, updateIndicator, isMobileView]);

  // 현재 섹션이 스크롤 경계에 있는지 확인
  const isSectionAtBoundary = useCallback((direction) => {
    const section = sectionRefs.current[currentIndexRef.current];
    if (!section) return true;

    const { scrollTop, scrollHeight, clientHeight } = section;
    const isScrollable = scrollHeight > clientHeight + 2;

    if (!isScrollable) return true; // 스크롤 불필요 → 바로 섹션 전환

    if (direction === "down") {
      return scrollTop + clientHeight >= scrollHeight - 2;
    } else {
      return scrollTop <= 2;
    }
  }, []);

  // Wheel 이벤트 (데스크톱만)
  useEffect(() => {
    if (isMobileView) return;

    const onWheel = (e) => {
      e.preventDefault();
      if (isAnimatingRef.current) return;

      wheelAccRef.current += e.deltaY;
      clearTimeout(wheelTimeoutRef.current);
      wheelTimeoutRef.current = setTimeout(() => {
        if (Math.abs(wheelAccRef.current) > 40) {
          if (wheelAccRef.current > 0) goNext();
          else goPrev();
        }
        wheelAccRef.current = 0;
      }, 30);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [goNext, goPrev, isMobileView]);

  // Touch 이벤트 (데스크톱/태블릿만)
  useEffect(() => {
    if (isMobileView) return;

    const onTouchStart = (e) => {
      touchStartYRef.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const diff = touchStartYRef.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goPrev();
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [goNext, goPrev, isMobileView]);

  // 키보드 이벤트 (데스크톱만)
  useEffect(() => {
    if (isMobileView) return;

    const onKeydown = (e) => {
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, [goNext, goPrev, isMobileView]);

  return (
    <div className="app-landing-v2 fullpage-mode">
      <Header
        variant="landing"
        scrolled={headerScrolled}
        onDark={headerOnDark}
      />

      {/* ===== Page Container (translateY로 풀페이지 이동) ===== */}
      <div
        className="page-container"
        ref={containerRef}
        style={{ transform: "translateY(0)" }}
      >
        {/* ===== 0. Hero (시안 2: 목업 2개) ===== */}
        <section
          className="scroll-section section-hero"
          ref={(el) => setSectionRef(el, 0)}
        >
          <div className="section-content hero-variant-2-active">
            <div className="hero-layout">
              <div className="hero-text">
                <div className="hero-badge scroll-reveal">
                  위고컴퍼니 '키즈다', SKT 키즈폰 2026 'ZEM' 킬러앱 탑재
                </div>
                <p className="hero-sub scroll-reveal">
                  No.1 어린이 시사 뉴스 앱
                </p>
                <h1 className="hero-title scroll-reveal">
                  매일 새로운 시사 뉴스와&nbsp;
                  <br />
                  <span className="gradient-text">학습 콘텐츠</span>
                </h1>
                <p className="hero-desc scroll-reveal">
                  아이 눈높이로 만나는 세상, AI와 함께하는&nbsp;
                  <br />
                  <strong>음성 다이어리 · 뉴스 요약 · 퀴즈 학습</strong>
                </p>
                <div className="hero-buttons scroll-reveal">
                  <a
                    href="https://apps.apple.com/kr/app/키즈다/id6738370393"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-store btn-store-filled"
                  >
                    <i className="fab fa-apple"></i>
                    <span>App Store</span>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.aslo.smartview.donga.kids"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-store btn-store-filled"
                  >
                    <i className="fab fa-google-play"></i>
                    <span>Google Play</span>
                  </a>
                </div>
              </div>
              <div className="hero-image hero-image-double scroll-reveal">
                <img
                  src={imageHero1}
                  alt="키즈다 로고 화면"
                  className="hero-mock hero-mock-left"
                />
                <img
                  src={imageHero2}
                  alt="키즈다 메인 화면"
                  className="hero-mock hero-mock-right"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 1. 키즈다 소개 ===== */}
        <section
          className="scroll-section section-intro"
          ref={(el) => setSectionRef(el, 1)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image01}
                  alt="키즈다 앱"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <h3 className="section-label scroll-reveal">키즈다</h3>
                <h2 className="section-title scroll-reveal">
                  60년 전통, 믿을 수 있는&nbsp;
                  <br />
                  어린이 전문기자 뉴스
                </h2>
                <p className="section-desc scroll-reveal">
                  어린이를 위한 시사 콘텐츠를 아이 눈높이에 맞춰 쉽고 바르게
                  전달합니다. 매일 새롭게 업데이트되는 뉴스로 세상을 읽는 힘을
                  길러보세요.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 2. 01 뉴스 ===== */}
        <section
          className="scroll-section section-news bg-soft"
          ref={(el) => setSectionRef(el, 2)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image01_1}
                  alt="키즈다 뉴스"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <h3 className="section-label scroll-reveal">01 뉴스</h3>
                <h2 className="section-title scroll-reveal">
                  뉴스로 배우고,
                  <br />
                  영어까지 익히는 학습 경험
                </h2>
                <div className="news-desc-row scroll-reveal">
                  <p className="section-desc">
                    교과와 시사, 영어까지 —
                    <br />
                    하나의 뉴스로 모두 챙길수 있어요.
                  </p>
                  <a
                    href="/product"
                    className="btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/product");
                    }}
                  >
                    <span>뉴스 구독하러 가기</span>
                    <i className="fas fa-play"></i>
                  </a>
                </div>

                {/* 시안 C: 넘버 그리드형 */}
                <div className="news-c scroll-reveal">
                  <div className="news-c-item news-c-wide">
                    <span className="news-c-num">01</span>
                    <div className="news-c-content">
                      <div className="news-c-head">
                        <span className="news-c-title">영어 번역</span>
                        <span className="new-badge">NEW</span>
                      </div>
                      <p className="news-c-desc">
                        뉴스 기사를 영어로 번역해 영어 학습까지 한 번에
                      </p>
                    </div>
                    <i className="fas fa-language news-c-bg-icon"></i>
                  </div>
                  <div className="news-c-grid">
                    <div className="news-c-item">
                      <span className="news-c-num">02</span>
                      <div className="news-c-content">
                        <div className="news-c-head">
                          <span className="news-c-title">교과 연계</span>
                        </div>
                        <p className="news-c-desc">
                          국어, 사회, 과학, 수학 전 과목 연계
                        </p>
                      </div>
                      <i className="fas fa-chalkboard-teacher news-c-bg-icon"></i>
                    </div>
                    <div className="news-c-item">
                      <span className="news-c-num">03</span>
                      <div className="news-c-content">
                        <div className="news-c-head">
                          <span className="news-c-title">단어 설명</span>
                        </div>
                        <p className="news-c-desc">어려운 시사 용어도 척척</p>
                      </div>
                      <i className="fas fa-spell-check news-c-bg-icon"></i>
                    </div>
                    <div className="news-c-item">
                      <span className="news-c-num">04</span>
                      <div className="news-c-content">
                        <div className="news-c-head">
                          <span className="news-c-title">AI 요약</span>
                        </div>
                        <p className="news-c-desc">
                          핵심 내용을 쉽고 빠르게 파악
                        </p>
                      </div>
                      <i className="fas fa-robot news-c-bg-icon"></i>
                    </div>
                    <div className="news-c-item">
                      <span className="news-c-num">05</span>
                      <div className="news-c-content">
                        <div className="news-c-head">
                          <span className="news-c-title">AI 퀴즈</span>
                        </div>
                        <p className="news-c-desc">학습 이해도를 즉시 확인</p>
                      </div>
                      <i className="fas fa-lightbulb news-c-bg-icon"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 3. 02 키즈 루틴 ===== */}
        <section
          className="scroll-section section-routine"
          ref={(el) => setSectionRef(el, 3)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image02}
                  alt="키즈 루틴"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <div className="label-row scroll-reveal">
                  <span className="new-badge">NEW</span>
                  <h3 className="section-label">02 키즈 루틴</h3>
                </div>
                <h2 className="section-title scroll-reveal">
                  우리 아이
                  <br />
                  하루 습관 관리
                </h2>
                <p className="section-desc scroll-reveal">
                  기상, 공부, 독서, 생활습관까지 아이의 일상을 스스로 관리할 수
                  있도록 도와주는 루틴 기능을 제공합니다.
                </p>
                {/* <div className="user-proof scroll-reveal">
                  <div className="avatar-stack">
                    <img src={imageAvatar1} alt="" className="avatar" />
                    <img src={imageAvatar2} alt="" className="avatar" />
                    <img src={imageAvatar3} alt="" className="avatar" />
                  </div>
                  <span className="proof-text">
                    이미 10,000명 이상의 아이들이 기록 중!
                  </span>
                </div> */}
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4. 03 AI 음성 일기 ===== */}
        <section
          className="scroll-section section-diary bg-soft"
          ref={(el) => setSectionRef(el, 4)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image03}
                  alt="AI 음성 일기"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <div className="label-row scroll-reveal">
                  <span className="new-badge">NEW</span>
                  <h3 className="section-label">03 AI 음성 일기</h3>
                </div>
                <h2 className="section-title scroll-reveal">
                  말하는 순간 완성되는&nbsp;
                  <br />
                  AI 음성 일기
                </h2>
                <p className="section-desc scroll-reveal">
                  아이의 음성을 AI가 자연스럽게 정리해&nbsp;
                  <br />
                  하루의 생각과 감정을 기록으로 남길 수 있습니다.
                </p>
                {/* <div className="user-proof scroll-reveal">
                  <div className="avatar-stack">
                    <img src={imageAvatar1} alt="" className="avatar" />
                    <img src={imageAvatar2} alt="" className="avatar" />
                    <img src={imageAvatar3} alt="" className="avatar" />
                  </div>
                  <span className="proof-text">
                    이미 10,000명 이상의 아이들이 기록 중!
                  </span>
                </div> */}
              </div>
            </div>
          </div>
        </section>

        {/* ===== 5. 04 키즈 마켓 ===== */}
        <section
          className="scroll-section section-market"
          ref={(el) => setSectionRef(el, 5)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image04}
                  alt="키즈 마켓"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <div className="label-row scroll-reveal">
                  <span className="new-badge">NEW</span>
                  <h3 className="section-label">04 키즈 마켓</h3>
                </div>
                <h2 className="section-title scroll-reveal">
                  보상으로 연결되는&nbsp;
                  <br />
                  동기부여, 키즈 마켓
                </h2>
                <p className="section-desc scroll-reveal">
                  학습과 루틴을 통해 모은 키즈별로 부모가 등록한 보상과 교환하며
                  즐겁게 습관을 만들어갈 수 있어요.
                </p>
                <div className="check-list scroll-reveal">
                  <div className="check-item">
                    <i className="fas fa-check-circle"></i>
                    <span>부모님이 직접 설정하는 맞춤 보상 목록</span>
                  </div>
                  <div className="check-item">
                    <i className="fas fa-check-circle"></i>
                    <span>스스로 목표를 세우는 경제 관념 학습</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 6. 05 에듀클립 ===== */}
        <section
          className="scroll-section section-educlip bg-soft"
          ref={(el) => setSectionRef(el, 6)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image05}
                  alt="에듀클립"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <h3 className="section-label scroll-reveal">05 에듀클립</h3>
                <h2 className="section-title scroll-reveal">
                  실전 학습 콘텐츠,&nbsp;
                  <br />
                  에듀클립 온라인 특강
                </h2>
                <p className="section-desc scroll-reveal">
                  전국 영어 · 수학 학력경시대회 완벽 대비!&nbsp;
                  <br />
                  핵심 기출문제 강의로 아이의 실력과 자신감을 키워줍니다.
                </p>
                <a
                  href="/educlip"
                  className="btn-primary scroll-reveal"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/educlip");
                  }}
                >
                  <span>특강 바로가기</span>
                  <i className="fas fa-play"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 7. 06 브레인랩 ===== */}
        <section
          className="scroll-section section-brainlab"
          ref={(el) => setSectionRef(el, 7)}
        >
          <div className="section-content">
            <div className="feature-layout feature-layout-phone">
              <div className="phone-mockup-wrap scroll-reveal">
                <img
                  src={image06}
                  alt="브레인랩"
                  className="phone-screen-img"
                />
              </div>
              <div className="feature-text">
                <h3 className="section-label scroll-reveal">06 브레인랩</h3>
                <h2 className="section-title scroll-reveal">
                  문해력과 수해력을 함께&nbsp;
                  <br />
                  키우는 브레인랩
                </h2>
                <p className="section-desc scroll-reveal">
                  매일 교차로 제공되는 문해력·수해력 문제를 풀며&nbsp;
                  <br />
                  아이의 사고력과 문제 해결력을 자연스럽게 높일 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 8. 키즈다 크루 ===== */}
        <section
          className="scroll-section section-crew bg-crew"
          ref={(el) => setSectionRef(el, 8)}
        >
          <div className="section-content">
            <div className="crew-layout">
              <div className="crew-text">
                <h3 className="section-label scroll-reveal">키즈다 크루</h3>
                <h2 className="section-title scroll-reveal">
                  배우고, 기록하고, 함께 자라는&nbsp;
                  <br />
                  키즈다 크루
                </h2>
                <p className="section-desc scroll-reveal">
                  키즈다 크루 캐릭터들과 함께&nbsp;
                  <br />
                  뉴스를 읽고, 퀴즈를 풀고, 루틴을 실천하며&nbsp;
                  <br />더 즐겁고 친근하게 학습 습관을 만들어갈 수 있습니다.
                </p>
              </div>
              <div className="crew-image scroll-reveal">
                <img src={imageCrew} alt="키즈다 크루 캐릭터" />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 9. 한줄 요약 + 통계 ===== */}
        <section
          className="scroll-section section-summary"
          ref={(el) => setSectionRef(el, 9)}
        >
          <div className="section-content">
            <h2 className="summary-quote scroll-reveal">
              "키즈다는 뉴스, 학습, 습관, 기록, 보상을 하나로 연결한&nbsp;
              <br />
              <span className="highlight">AI 기반 어린이 맞춤 학습 플랫폼</span>
              입니다."
            </h2>
            <div className="stats-row scroll-reveal">
              <div className="stat-item">
                <strong>3,000+</strong>
                <span>누적 다운로드</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <strong>4/5</strong>
                <span>학부모 만족도</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <strong>2,500+</strong>
                <span>어린이 시사 기사</span>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 10. Footer ===== */}
        <section
          className="scroll-section section-footer-wrap"
          ref={(el) => setSectionRef(el, 10)}
        >
          <Footer />
        </section>
      </div>

      {/* ===== Side Navigation ===== */}
      <nav className={`side-nav ${isOnDark ? "side-nav-on-dark" : ""}`}>
        <ul className="side-nav-list">
          {SECTION_LABELS.map((_, idx) => (
            <li
              key={idx}
              className={`side-nav-item ${activeIndex === idx ? "active" : ""}`}
              onClick={() => scrollToSection(idx)}
            >
              <button
                className="side-nav-bar"
                aria-label={SECTION_LABELS[idx]}
              ></button>
            </li>
          ))}
        </ul>
        <div className="side-nav-panel">
          {SECTION_LABELS.map((label, idx) => (
            <a
              key={idx}
              href="#"
              className={`panel-item ${activeIndex === idx ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(idx);
              }}
            >
              {label}
            </a>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default AppLanding;
